import { LatLngLiteral } from 'components/Scene'
import { ReactNode } from 'react'
import { Vector3 } from 'three'

export interface Slide {
  id: string
  title: string
  visualizationType: 'model' | 'area' | 'marker'
  description?: string | ReactNode
  properties?: {
    audienceCapacity: string
    purpose: string[]
    hasElectricity: boolean | undefined
    hasRestaurantOrBar: boolean | undefined
    stageDimensions?: {
      width: number
      depth: number
      height: number
    }
    website: string
  }
  images: string[]
  model?: {
    position: Vector3
    scale: number
    rotationZ: number
    modelPath: string
  }
  cameraParameters: {
    targetPosition: LatLngLiteral
    heading: number
    tilt: number
    zoom: number
  }
}

export const startPosition: LatLngLiteral = {
  lat: 58.5890541557113,
  lng: 16.18038779594937,
}

export const defaultCameraPosition = {
  targetPosition: startPosition,
  zoom: 18,
  tilt: 19,
  heading: 0,
}

export const getSlides = () => {
  const slides: Slide[] = [
    {
      id: 'norrköping',
      title: 'Norrköping',
      visualizationType: 'model',
      description: (
        <>
          Letar du efter scener att boka i Norrköping? Här hittar du en lista på de flesta scener i staden tillsammans med lite information.
          Om du har några frågor kontakta oss gärna på <a href='mailto:evenemang@norrkoping.se'>evenemang@norrkoping.se</a>.
        </>
      ),
      images: ['norrkoping-1.jpg', 'norrkoping-2.jpg'],
      cameraParameters: {
        heading: 0,
        tilt: 19,
        zoom: 17,
        targetPosition: {
          lat: 58.58839823942526,
          lng: 16.181053454895764,
        },
      },
    },
    {
      id: 'arbetets museum',
      title: 'Arbetets museum',
      visualizationType: 'model',
      description:
        'Dukade långbord, sittande konsertpublik eller gungande dansgolv. I Flygeln, Norrköpings modernaste konsert- och konferenslokal, är det bara fantasin som sätter gränser. Med sittplatsläktare som smidigt kan hissas upp och döljas i taket öppnar sig nya möjligheter. Konferera på dagen, efter ombyte och mingel har lokalen bytt skepnad till glittrande festplats! På scenen spelar ofta stora stjärnor, men på konferensen är du stjärnan.',
      images: ['arbetetsmuseum-1.jpg', 'arbetetsmuseum-2.jpg', 'arbetetsmuseum-3.jpg'],
      properties: {
        hasElectricity: true,
        hasRestaurantOrBar: true,
        audienceCapacity: '14 / 40 / 60 / 160 personer',
        purpose: ['Scenframträdande', 'Konferens'],
        website: 'https://www.arbetetsmuseum.se/boka-konferens/',
      },
      model: {
        modelPath: '/models/arbetets museum.glb',
        position: new Vector3(0, 0, 0),
        scale: 22.0,
        rotationZ: 0,
      },
      cameraParameters: {
        heading: 90,
        tilt: 55,
        zoom: 19,
        targetPosition: {
          lat: 58.58910924273712,
          lng: 16.179995848859686,
        },
      },
    },
    {
      id: 'värmekyrkan',
      title: 'Värmekyrkan',
      visualizationType: 'model',
      description: `Ett stenkast från huvudbyggnaden Louis De Geer hittar vi Värmekyrkan. Detta är en lokal för dig som vill förbluffa – en rå industrimiljö med arkitektur, akustik och ljusspel som i en medeltida katedral. En plats med högt i tak där festblåsa möter betong och där modern teknik skapar rätt stämning.`,
      images: ['varmekyrkan-1.jpg', 'varmekyrkan-2.jpg', 'varmekyrkan-3.jpg'],
      properties: {
        hasElectricity: true,
        hasRestaurantOrBar: false,
        audienceCapacity: '500 / 1 000 personer',
        purpose: ['Scenframträdande', 'Konferens', 'Bröllop'],
        website: 'https://louisdegeer.se/konferens/lokaler/varmekyrkan/',
      },
      model: {
        modelPath: '/models/varmekyrkan color.glb',
        position: new Vector3(17, -10, 0),
        scale: 7.5,
        rotationZ: -0.5,
      },
      cameraParameters: {
        heading: 0,
        tilt: 55,
        zoom: 19,
        targetPosition: {
          lat: 58.5885,
          lng: 16.1807,
        },
      },
    },
    {
      id: 'holmentorget',
      title: 'Holmentorget',
      visualizationType: 'model',
      description:
        'Torg i kärnan av Norrköpings som fungerar bra för utomhuskonserter och andra evenemang. Här finns möjlighet att sätta upp en scen.',
      images: ['holmentorget-1.jpg'],
      properties: {
        hasElectricity: true,
        hasRestaurantOrBar: undefined,
        audienceCapacity: '600 - 1 500 personer',
        purpose: ['Scenframträdande'],
        website: 'https://www.norrkoping.se/arbete-och-naringsliv/lokaler-mark-och-byggande/anvanda-stadens-mark',
      },
      model: {
        modelPath: '/models/holmentorget.glb',
        position: new Vector3(0, 0, 0),
        scale: 8.0,
        rotationZ: 0,
      },
      cameraParameters: {
        heading: 50,
        tilt: 50,
        zoom: 20,
        targetPosition: {
          lat: 58.58805340736848,
          lng: 16.18165044605633,
        },
      },
    },
    {
      id: 'flygeln',
      title: 'Flygeln',
      visualizationType: 'model',
      description:
        'Dukade långbord, sittande konsertpublik eller gungande dansgolv. I Flygeln, Norrköpings modernaste konsert- och konferenslokal, är det bara fantasin som sätter gränser. Med sittplatsläktare som smidigt kan hissas upp och döljas i taket öppnar sig nya möjligheter. Konferera på dagen, efter ombyte och mingel har lokalen bytt skepnad till glittrande festplats!',
      images: ['flygeln-1.jpg', 'flygeln-2.jpg', 'flygeln-3.jpg'],
      properties: {
        hasElectricity: true,
        hasRestaurantOrBar: true,
        audienceCapacity: '600 / 1 500 personer',
        purpose: ['Scenframträdande'],
        website: 'https://louisdegeer.se/konferens/lokaler/flygeln/',
      },
      model: {
        modelPath: '/models/flygeln.glb',
        position: new Vector3(0, 0, 0),
        scale: 16.0,
        rotationZ: 2.5,
      },
      cameraParameters: {
        heading: 60,
        tilt: 55,
        zoom: 19,
        targetPosition: {
          lat: 58.587891501780796,
          lng: 16.182304117362218,
        },
      },
    },
    {
      id: 'de geer',
      title: 'Louis De Geer Konsert & Kongress',
      visualizationType: 'model',
      description:
        'Stor lokal och salong öppnar famnen för konferenser och kongresser med krav på förstklassig akustik och gott om plats på båda sidor om orkesterdiket. Omfattande ljus-, ljud- och videoteknik samt flexibel möblering med skrivbordsmöjligheter gör lokalen anpassningsbar och lyhörd för dina arrangörsbehov. Våra skickliga tekniker hjälper dig självklart genom hela arrangemanget.',
      images: ['de geer-0.jpg', 'de geer-1.jpg', 'de geer-2.jpg'],
      properties: {
        hasElectricity: true,
        hasRestaurantOrBar: true,
        audienceCapacity: '1 378 personer (729 parkett)',
        purpose: ['Scenframträdande'],
        website: 'https://louisdegeer.se/konferens/lokaler/de-geerhallen/',
      },
      model: {
        modelPath: '/models/de geer color.glb',
        position: new Vector3(0, 0, 0),
        scale: 33,
        rotationZ: 2.83,
      },
      cameraParameters: {
        heading: -20,
        tilt: 55,
        zoom: 19,
        targetPosition: {
          lat: 58.587974335661876,
          lng: 16.183664665128706,
        },
      },
    },
    {
      id: 'kråkholmen',
      title: 'Kråkholmen',
      visualizationType: 'model',
      description: 'Halvö intill konserthuset. Funkar bra för för mysiga, mindre utomhuskonserter.',
      images: ['krakholmen-1.jpg', 'krakholmen-2.jpg'],
      properties: {
        hasElectricity: true,
        hasRestaurantOrBar: true,
        audienceCapacity: '300 personer',
        purpose: ['Scenframträdande', 'Bröllop'],
        website: 'https://louisdegeer.se/konferens/lokaler/krakholmen/',
      },
      model: {
        modelPath: '/models/krakholmen.glb',
        position: new Vector3(0, 0, 0),
        scale: 8.0,
        rotationZ: 2.8,
      },
      cameraParameters: {
        heading: 160,
        tilt: 55,
        zoom: 20,
        targetPosition: {
          lat: 58.588428791133424,
          lng: 16.18403221303297,
        },
      },
    },
    {
      id: 'knäppingsborg',
      title: 'Knäppingsborg',
      visualizationType: 'model',
      description: 'Centralt område med butiker, restauranger och mycket bra för kvällskonserter.',
      images: ['knappingsborg-1.jpg', 'knappingsborg-2.jpg'],
      properties: {
        hasElectricity: true,
        hasRestaurantOrBar: true,
        audienceCapacity: '250 personer',
        purpose: ['Scenframträdande'],
        website: 'https://knappingsborg.se/event/',
      },
      model: {
        modelPath: '/models/knappingsborg.glb',
        position: new Vector3(0, 0, 0),
        scale: 30.0,
        rotationZ: 0,
      },
      cameraParameters: {
        heading: 60,
        tilt: 55,
        zoom: 19,
        targetPosition: {
          lat: 58.589353101719084,
          lng: 16.185412779407578,
        },
      },
    },
    {
      id: 'hörsalen',
      title: 'Hörsalen',
      visualizationType: 'model',
      description: 'Inomhus-scen i centrala Norrköping. Sittplatser.',
      images: ['horsalen-1.jpg', 'horsalen-2.jpg', 'horsalen-3.jpg'],
      properties: {
        hasElectricity: true,
        hasRestaurantOrBar: false,
        audienceCapacity: '306 personer',
        purpose: ['Scenframträdande'],
        website: 'https://norrkoping.se/se-och-gora/uthyrning/horsalen',
      },
      model: {
        modelPath: '/models/horsalen.glb',
        position: new Vector3(0, 0, 0),
        scale: 20.0,
        rotationZ: 0,
      },
      cameraParameters: {
        heading: 60,
        tilt: 55,
        zoom: 19,
        targetPosition: {
          lat: 58.58830127672807,
          lng: 16.187770435415317,
        },
      },
    },
    {
      id: 'elite grand hotel',
      title: 'Bankiren, Elite Grand Hotel',
      visualizationType: 'model',
      description: 'Hotell med bra konferens och konsert-möjligheter.',
      images: [
        'grandhotell-1.jpg',
        'grandhotell-2.jpg',
        'grandhotell-3.jpg',
        'grandhotell-4.jpg',
        'grandhotell-5.jpg',
        'grandhotell-6.jpg',
      ],
      properties: {
        hasElectricity: true,
        hasRestaurantOrBar: true,
        audienceCapacity: '30 / 40 /60 / 150 / 200 personer',
        purpose: ['Konferens', 'Scenframträdande'],
        website: 'https://elite.se/sv/konferens/norrkoping/grand-hotel/',
      },
      model: {
        modelPath: '/models/elite grand hotel.glb',
        position: new Vector3(0, 0, 0),
        scale: 35.0,
        rotationZ: 0,
      },
      cameraParameters: {
        heading: 130,
        tilt: 55,
        zoom: 19,
        targetPosition: {
          lat: 58.59301415867088,
          lng: 16.186427448063707,
        },
      },
    },
    {
      id: 'hedvigs kyrka',
      title: 'Hedvigs Kyrka',
      visualizationType: 'model',
      description: 'Fin kyrka i centrala Norrköping med enligt vissa - den bästa akustiken i stan. ',
      images: ['hedvigskyrka-1.jpg', 'hedvigskyrka-2.jpg', 'hedvigskyrka-3.jpg'],
      properties: {
        hasElectricity: true,
        hasRestaurantOrBar: true,
        audienceCapacity: '400 personer',
        purpose: ['Scenframträdande', 'Bröllop'],
        website: 'https://www.svenskakyrkan.se/norrkoping/hedvigs-kyrka',
      },
      model: {
        modelPath: '/models/hedvigskyrka.glb',
        position: new Vector3(0, 0, 0),
        scale: 16.0,
        rotationZ: 0,
      },
      cameraParameters: {
        heading: 60,
        tilt: 55,
        zoom: 20,
        targetPosition: {
          lat: 58.59277946787805,
          lng: 16.18787883118804,
        },
      },
    },
    {
      id: 'hugo',
      title: 'Hugo Stekhus Klubb och Bowling',
      visualizationType: 'model',
      description: 'Klubb med dansgolv och bar. Hugo är en av Norrköpings största nattklubbar och har en relativt stor scen.',
      images: ['hugo-1.jpg', 'hugo-2.jpg', 'hugo-3.jpg'],
      properties: {
        hasElectricity: true,
        hasRestaurantOrBar: true,
        audienceCapacity: '600 - 1 500 personer',
        purpose: ['Scenframträdande'],
        website: 'https://www.hugonorrkoping.se/nojen/kontakta-oss/',
      },
      model: {
        modelPath: '/models/hugo.glb',
        position: new Vector3(4, 2, 0),
        scale: 22.0,
        rotationZ: 0,
      },
      cameraParameters: {
        heading: -40,
        tilt: 55,
        zoom: 19,
        targetPosition: {
          lat: 58.59522977787076,
          lng: 16.18201183918975,
        },
      },
    },
    {
      id: 'östgötateatern',
      title: 'Östgötateatern',
      visualizationType: 'model',
      description: 'Stans största teater. Stor scen, vanligt med musikaler och andra lite större framträdanden.',
      images: ['ostgotateatern-1.jpg', 'ostgotateatern-2.jpg', 'ostgotateatern-3.jpg'],
      properties: {
        hasElectricity: true,
        hasRestaurantOrBar: true,
        audienceCapacity: '600 personer',
        purpose: ['Scenframträdande'],
        website: 'https://www.ostgotateatern.se/kontakt',
      },
      model: {
        modelPath: '/models/ostgotateatern.glb',
        position: new Vector3(-10, 0, 0),
        scale: 16.0,
        rotationZ: 0,
      },
      cameraParameters: {
        heading: -90,
        tilt: 55,
        zoom: 19,
        targetPosition: {
          lat: 58.59409282368279,
          lng: 16.182085283131872,
        },
      },
    },
    {
      id: 'hallarna',
      title: 'Kulturkvarteret Hallarna',
      visualizationType: 'model',
      description: 'Lokala live-scenens mittpunkt. Här finns replokaler och flera scener för mindre konserter.',
      images: ['hallarna-1.jpg', 'hallarna-2.jpg', 'hallarna-3.jpg'],
      properties: {
        hasElectricity: true,
        hasRestaurantOrBar: true,
        audienceCapacity: '30 / 40 / 50 / 75 / 85 / 120 / 300 personer',
        purpose: ['Scenframträdande', 'Workshop'],
        website: 'https://www.kulturkvarterethallarna.se/kontakt/',
      },
      model: {
        modelPath: '/models/hallarna.glb',
        position: new Vector3(0, 0, 0),
        scale: 10.0,
        rotationZ: 0,
      },
      cameraParameters: {
        heading: -50,
        tilt: 55,
        zoom: 19,
        targetPosition: {
          lat: 58.59350892494891,
          lng: 16.17812449775564,
        },
      },
    },
    {
      id: 'visualiseringscenter c',
      title: 'Visualiseringscenter C',
      visualizationType: 'model',
      description: 'Visualiseringscenter C är en mötesplats kring visualisering mitt i Industrilandskapet i Norrköping.',
      images: ['visualiseringscenter-1.jpg', 'visualiseringscenter-2.jpg', 'visualiseringscenter-3.jpg', 'visualiseringscenter-4.jpg'],
      properties: {
        hasElectricity: true,
        hasRestaurantOrBar: true,
        audienceCapacity: '15 / 25 / 99 / 150 personer',
        purpose: ['Konferens', 'Visualisering'],
        website: 'https://visualiseringscenter.se/bokningsforfragan',
      },
      model: {
        modelPath: '/models/visualiseringscenter.glb',
        position: new Vector3(-14, -20, 0),
        scale: 29.0,
        rotationZ: -0.35,
      },
      cameraParameters: {
        heading: 130,
        tilt: 55,
        zoom: 19,
        targetPosition: {
          lat: 58.588995598607916,
          lng: 16.177347373564956,
        },
      },
    },
    {
      id: 'cnema',
      title: 'Cnema',
      visualizationType: 'model',
      description: 'CNEMA är Norrköpings kommuns filmverksamhet och hör till Kultur- och fritidsnämnden i Norrköping.',
      images: ['cnema-1.jpg', 'cnema-2.jpg', 'cnema-3.jpg', 'cnema-4.jpg'],
      properties: {
        hasElectricity: undefined,
        hasRestaurantOrBar: true,
        audienceCapacity: '40 / 65 / 192 personer',
        purpose: ['Konferens'],
        website: 'https://cnema.se/bio/hyr-cnema/konferens/#boka',
      },
      model: {
        modelPath: '/models/cnema.glb',
        position: new Vector3(-14, -20, 0),
        scale: 29.0,
        rotationZ: -0.35,
      },
      cameraParameters: {
        heading: -30,
        tilt: 55,
        zoom: 19,
        targetPosition: {
          lat: 58.588995598607916,
          lng: 16.177347373564956,
        },
      },
    },
    {
      id: 'arbis',
      title: 'Arbis Norrköping',
      visualizationType: 'model',
      description: 'Stor live-scen nära Norrköpings Universitet.',
      images: ['arbis-1.jpg', 'arbis-2.jpg', 'arbis-3.jpg'],
      properties: {
        hasElectricity: true,
        hasRestaurantOrBar: true,
        audienceCapacity: '900 personer',
        purpose: ['Scenframträdande', 'Konferens'],
        website: 'https://www.arbis.se/contact',
      },
      model: {
        modelPath: '/models/arbis.glb',
        position: new Vector3(0, -2, 0),
        scale: 12.0,
        rotationZ: 0,
      },
      cameraParameters: {
        heading: -30,
        tilt: 55,
        zoom: 19,
        targetPosition: {
          lat: 58.59012445076691,
          lng: 16.174936736112286,
        },
      },
    },
    {
      id: 'harrys',
      title: 'Harrys',
      visualizationType: 'model',
      description: 'Festlokal med bra möjligheter för live-musik och dansgolv.',
      images: ['harrys-1.jpg', 'harrys-2.jpg', 'harrys-3.jpg'],
      properties: {
        hasElectricity: true,
        hasRestaurantOrBar: true,
        audienceCapacity: '150 / 450 personer',
        purpose: ['Scenframträdande', 'Festlokal'],
        website: 'https://www.bronsalonger.se/kontakt',
      },
      model: {
        modelPath: '/models/harrys.glb',
        position: new Vector3(5, 0, 0),
        scale: 14.0,
        rotationZ: 0,
      },
      cameraParameters: {
        heading: 15,
        tilt: 55,
        zoom: 20,
        targetPosition: {
          lat: 58.589563591879696,
          lng: 16.180767454452365,
        },
      },
    },

    {
      id: 'idrottsparken',
      title: 'Platinum Cars Arena',
      visualizationType: 'model',
      description:
        'PlatinumCars Arena, tidigare benämnd Norrköpings Idrottspark, Nya Parken och Östgötaporten, i folkmun "Parken", är en fotbollsarena i Norrköping.',
      images: ['idrottsparken-1.jpg', 'idrottsparken-2.jpg', 'idrottsparken-3.jpg'],
      properties: {
        hasElectricity: true,
        hasRestaurantOrBar: true,
        audienceCapacity: '250 / 16 000 personer',
        purpose: ['Konferens', 'Scenframträdande', 'Idrott'],
        website: 'https://boka.bokad.se/ifknorrkoping',
      },
      model: {
        modelPath: '/models/idrottsparken.glb',
        position: new Vector3(10, 13, 0),
        scale: 65.0,
        rotationZ: 0,
      },
      cameraParameters: {
        heading: 60,
        tilt: 55,
        zoom: 18,
        targetPosition: {
          lat: 58.58432179481897,
          lng: 16.172990923203887,
        },
      },
    },
    {
      id: 'himmelstalundshallen',
      title: 'Himmelstalundshallen',
      visualizationType: 'model',
      description:
        'Himmelstalundshallen är en ishall i Norrköping i Sverige. Ishallen är hemmaarena för HC Vita Hästen och Norrköpings konståkningsklubb, och har en publikkapacitet på 4 500 personer. ',
      images: ['himmelstalundshallen-1.jpg', 'himmelstalundshallen-2.jpg'],
      properties: {
        hasElectricity: true,
        hasRestaurantOrBar: false,
        audienceCapacity: '4 500 personer',
        purpose: ['Idrott', 'Scenframträdande'],
        website: 'https://norrkoping.se/se-och-gora/uthyrning/idrottsanlaggningar',
      },
      model: {
        modelPath: '/models/himmelstalundshallen.glb',
        position: new Vector3(0, 0, 0),
        scale: 40.0,
        rotationZ: 0,
      },
      cameraParameters: {
        heading: 60,
        tilt: 55,
        zoom: 18,
        targetPosition: {
          lat: 58.58771382016308,
          lng: 16.14012025394681,
        },
      },
    },
    {
      id: 'stadium arena',
      title: 'Stadium Arena',
      visualizationType: 'model',
      description:
        'Stadium Arena är en idrottsanläggning i Norrköping. Den invigdes i december 2008. Anläggningen har en friidrotthall och en baskethall samt en träningshall. Arenan används även för konserter.',
      images: ['stadiumarena-1.jpg', 'stadiumarena-2.jpg', 'stadiumarena-3.jpg'],
      properties: {
        hasElectricity: true,
        hasRestaurantOrBar: false,
        audienceCapacity: '3 500 personer',
        purpose: ['Idrott', 'Scenframträdande'],
        website: 'https://www.dolphins.se/stadiumarena',
      },
      model: {
        modelPath: '/models/stadiumarena.glb',
        position: new Vector3(0, 0, 0),
        scale: 50.0,
        rotationZ: -0.7,
      },
      cameraParameters: {
        heading: 60,
        tilt: 55,
        zoom: 18,
        targetPosition: {
          lat: 58.587149665598716,
          lng: 16.143645975975915,
        },
      },
    },
    {
      id: 'vasaparken',
      title: 'Vasaparken',
      visualizationType: 'area',
      description:
        'Vasaparken är en av Norrköpings två stadsparker och ligger vid Södra promenaden. Vasaparken är en populär park för samvaro, lek och aktiviteter men också för större evenemang som vänder sig till hela staden.',
      images: ['vasaparken-1.jpg', 'vasaparken-2.jpg'],
      properties: {
        hasElectricity: false,
        hasRestaurantOrBar: undefined,
        audienceCapacity: 'ca 10 000 personer',
        purpose: ['Scenframträdande'],
        website: 'https://www.norrkoping.se/arbete-och-naringsliv/lokaler-mark-och-byggande/anvanda-stadens-mark',
      },
      model: {
        modelPath: '/models/vasaparken.glb',
        position: new Vector3(0, 0, 0),
        scale: 50.0,
        rotationZ: -0.2,
      },
      cameraParameters: {
        heading: 60,
        tilt: 55,
        zoom: 18,
        targetPosition: {
          lat: 58.58342814740364,
          lng: 16.19358875012728,
        },
      },
    },
    {
      id: 'bråvallafältet',
      title: 'Bråvallafältet',
      visualizationType: 'area',
      description: 'Stort grönområde bra för större festivaler. ',
      images: ['bravalla-1.jpg', 'bravalla-2.jpg', 'bravalla-3.jpg'],
      properties: {
        hasElectricity: false,
        hasRestaurantOrBar: undefined,
        audienceCapacity: 'ca 120 000 personer',
        purpose: ['Scenframträdande'],
        website: 'https://www.norrkoping.se/arbete-och-naringsliv/lokaler-mark-och-byggande/anvanda-stadens-mark',
      },
      model: {
        modelPath: '/models/bravallafaltet.glb',
        position: new Vector3(0, 0, 0),
        scale: 65.0,
        rotationZ: 0,
      },
      cameraParameters: {
        heading: 60,
        tilt: 55,
        zoom: 16,
        targetPosition: {
          lat: 58.610151545782706,
          lng: 16.11225238687069,
        },
      },
    },
    {
      id: 'lejongropen',
      title: 'Lejongropen',
      visualizationType: 'area',
      description: 'Lokalt grönområde i Folkparken som funkar för enklare utomhuskonserter.',
      images: ['lejongropen-1.jpg'],
      properties: {
        hasElectricity: false,
        hasRestaurantOrBar: undefined,
        audienceCapacity: 'ca 4 000 personer',
        purpose: ['Scenframträdande'],
        website: 'https://www.norrkoping.se/arbete-och-naringsliv/lokaler-mark-och-byggande/anvanda-stadens-mark',
      },
      model: {
        modelPath: '/models/lejongropen.glb',
        position: new Vector3(0, 0, 0),
        scale: 65.0,
        rotationZ: 0,
      },
      cameraParameters: {
        heading: 60,
        tilt: 55,
        zoom: 18,
        targetPosition: {
          lat: 58.591735980797935,
          lng: 16.16469168216868,
        },
      },
    },
    {
      id: 'himmelstalundsfältet',
      title: 'Himmelstalundsfältet',
      visualizationType: 'area',
      description: 'Stort grönområde som skulle kunna funka som festivalområde eller större konsertplats.',
      images: ['himmelstalundsfaltet-1.jpg', 'himmelstalundsfaltet-2.jpg'],
      properties: {
        hasElectricity: false,
        hasRestaurantOrBar: undefined,
        audienceCapacity: 'Saknar maxtak',
        purpose: ['Scenframträdande'],
        website: 'https://www.norrkoping.se/arbete-och-naringsliv/lokaler-mark-och-byggande/anvanda-stadens-mark',
      },
      model: {
        modelPath: '/models/himmelstalundsfaltet.glb',
        position: new Vector3(0, 0, 0),
        scale: 65.0,
        rotationZ: 0,
      },
      cameraParameters: {
        heading: 60,
        tilt: 55,
        zoom: 16,
        targetPosition: {
          lat: 58.59343899917011,
          lng: 16.146608046291497,
        },
      },
    },
    {
      id: 'johannisborgsruinen',
      title: 'Johannisborgs slottsruin',
      visualizationType: 'marker',
      description: 'Ruin som skulle kunna funka för mindre utomhuskonserter. Ligger strax utanför statskärnan.',
      images: ['johannisborg-1.jpg'],
      properties: {
        hasElectricity: false,
        hasRestaurantOrBar: false,
        audienceCapacity: '-',
        purpose: ['Scenframträdande'],
        website: 'https://www.norrkoping.se/arbete-och-naringsliv/lokaler-mark-och-byggande/anvanda-stadens-mark',
      },
      model: {
        modelPath: '/models/pin.glb',
        position: new Vector3(0, 0, 0),
        scale: 7.0,
        rotationZ: 0,
      },
      cameraParameters: {
        heading: 60,
        tilt: 55,
        zoom: 18,
        targetPosition: {
          lat: 58.600123815234966,
          lng: 16.197211768644895,
        },
      },
    },
    {
      id: 'borgsruinen',
      title: 'Borgsruinen',
      visualizationType: 'marker',
      description: 'Häftig ruin för mindre utomhuskonserter.',
      images: ['borgsruinen-1.jpg', 'borgsruinen-2.jpg', 'borgsruinen-3.jpg'],
      properties: {
        hasElectricity: false,
        hasRestaurantOrBar: false,
        audienceCapacity: '500 personer',
        purpose: ['Scenframträdande'],
        website: 'https://www.norrkoping.se/arbete-och-naringsliv/etablering-mark-och-byggande/anvanda-stadens-mark',
      },
      model: {
        modelPath: '/models/pin.glb',
        position: new Vector3(0, 0, 0),
        scale: 7.0,
        rotationZ: 0,
      },
      cameraParameters: {
        heading: 60,
        tilt: 55,
        zoom: 18,
        targetPosition: {
          lat: 58.58263446402512,
          lng: 16.14961740966929,
        },
      },
    },
    {
      id: 'vildmarkshotellet',
      title: 'Vildmarkshotellet',
      visualizationType: 'marker',
      description: 'Hotellet precis bredvid Kolmården. Här finns möjlighet att ha konferens och boende i samma byggnad.',
      images: [
        'vildmarkshotellet-1.jpg',
        'vildmarkshotellet-2.jpg',
        'vildmarkshotellet-3.jpg',
        'vildmarkshotellet-4.jpg',
        'vildmarkshotellet-5.jpg',
      ],
      properties: {
        hasElectricity: undefined,
        hasRestaurantOrBar: true,
        audienceCapacity: '8 / 30 / 50 / 80 / 130 / 400 / 1800 personer',
        purpose: ['Konferens'],
        website:
          'https://www.vildmarkshotellet.com/boka-konferensupplevelser?gclid=CjwKCAjwgZCoBhBnEiwAz35RwkeXQDiONoUe1LwlaUwurNqt3cJ8_vEZy8U9HHd79mxDGqauiuvjGRoC2fgQAvD_BwE',
      },
      model: {
        modelPath: '/models/pin.glb',
        position: new Vector3(0, 0, 0),
        scale: 7.0,
        rotationZ: 0,
      },
      cameraParameters: {
        heading: 60,
        tilt: 55,
        zoom: 18,
        targetPosition: {
          lat: 58.658673748796396,
          lng: 16.45170461834628,
        },
      },
    },
    {
      id: 'villa-fridhem',
      title: 'Villa Fridhem',
      visualizationType: 'marker',
      description: 'Villa med kunglig historia som idag är ett hotell med konferensmöjligheter. Ligger precis vid Bråviken.',
      images: ['villafridhem-1.jpg', 'villafridhem-2.jpg', 'villafridhem-3.jpg', 'villafridhem-4.jpg'],
      properties: {
        hasElectricity: undefined,
        hasRestaurantOrBar: true,
        audienceCapacity: '6 / 10 / 18 / 24 / 40 / 50 personer',
        purpose: ['Konferens'],
        website: 'https://villafridhem.se/konferens-norrkoping-villa-fridhem/',
      },
      model: {
        modelPath: '/models/pin.glb',
        position: new Vector3(0, 0, 0),
        scale: 7.0,
        rotationZ: 0,
      },
      cameraParameters: {
        heading: 60,
        tilt: 55,
        zoom: 18,
        targetPosition: {
          lat: 58.66957847253778,
          lng: 16.258166699106923,
        },
      },
    },
    {
      id: 'arkosunds-hotell',
      title: 'Arkösunds Hotell',
      visualizationType: 'marker',
      description:
        'Hotell med konferensmöjligheter. Ligger precis vid Bråviken. Här finns möjlighet att ha konferens och boende i samma byggnad.',
      images: ['arkosundshotell-1.jpg', 'arkosundshotell-2.jpg'],
      properties: {
        hasElectricity: undefined,
        hasRestaurantOrBar: true,
        audienceCapacity: '100 personer',
        purpose: ['Konferens', 'Bröllop'],
        website: 'https://arkosundshotell.se/hotellpaket/konferens/',
      },
      model: {
        modelPath: '/models/pin.glb',
        position: new Vector3(0, 0, 0),
        scale: 7.0,
        rotationZ: 0,
      },
      cameraParameters: {
        heading: 60,
        tilt: 55,
        zoom: 18,
        targetPosition: {
          lat: 58.48891932507786,
          lng: 16.940700903754815,
        },
      },
    },
    {
      id: 'evigheten',
      title: 'Evigheten Records',
      visualizationType: 'marker',
      description: 'Lokalt underground-ställe vars dansgolv ofta stänger sent.',
      images: ['evigheten-1.jpg'],
      properties: {
        hasElectricity: true,
        hasRestaurantOrBar: false,
        audienceCapacity: '100 personer',
        purpose: ['Scenframträdande'],
        website: 'https://www.facebook.com/evighetenrecords/?locale=sv_SE',
      },
      model: {
        modelPath: '/models/pin.glb',
        position: new Vector3(0, 0, 0),
        scale: 7.0,
        rotationZ: 0,
      },
      cameraParameters: {
        heading: 60,
        tilt: 55,
        zoom: 18,
        targetPosition: {
          lat: 58.59753604492366,
          lng: 16.179437012594978,
        },
      },
    },
    {
      id: 'masshallarna',
      title: 'Mässhallarna',
      visualizationType: 'marker',
      description: 'Mässhallarna är en idrottsanläggning som kan fungera för inomhuskonserter.',
      images: ['masshallarna-1.jpg'],
      properties: {
        hasElectricity: true,
        hasRestaurantOrBar: false,
        audienceCapacity: '800 / 1 200 personer',
        purpose: ['Idrott', 'Scenframträdande'],
        website: 'https://norrkoping.se/se-och-gora/uthyrning/idrottsanlaggningar',
      },
      model: {
        modelPath: '/models/pin.glb',
        position: new Vector3(0, 0, 0),
        scale: 7.0,
        rotationZ: 0,
      },
      cameraParameters: {
        heading: 60,
        tilt: 55,
        zoom: 18,
        targetPosition: {
          lat: 58.586786231443654,
          lng: 16.146843325249172,
        },
      },
    },
    {
      id: 'stopet',
      title: 'Stopet',
      visualizationType: 'marker',
      description: 'Lokal bar och populär live-scen.',
      images: ['stopet-1.jpg', 'stopet-2.jpg'],
      properties: {
        hasElectricity: true,
        hasRestaurantOrBar: true,
        audienceCapacity: '150 personer',
        purpose: ['Scenframträdande'],
        website: 'https://www.facebook.com/profile.php?id=100087009425036',
      },
      model: {
        modelPath: '/models/pin.glb',
        position: new Vector3(0, 0, 0),
        scale: 7.0,
        rotationZ: 0,
      },
      cameraParameters: {
        heading: 60,
        tilt: 55,
        zoom: 18,
        targetPosition: {
          lat: 58.58825105559973,
          lng: 16.185924675593448,
        },
      },
    },
    {
      id: 'lagerqvist',
      title: 'Lagerqvist Restaurang & Bar',
      visualizationType: 'marker',
      description: 'Restaurang med möjlighet till live-musik.',
      images: ['lagerqvist-1.jpg', 'lagerqvist-2.jpg', 'lagerqvist-3.jpg'],
      properties: {
        hasElectricity: true,
        hasRestaurantOrBar: true,
        audienceCapacity: '120 personer',
        purpose: ['Scenframträdande', 'Festlokal'],
        website: 'https://restauranglagerqvist.se/paraden/',
      },
      model: {
        modelPath: '/models/pin.glb',
        position: new Vector3(0, 0, 0),
        scale: 7.0,
        rotationZ: 0,
      },
      cameraParameters: {
        heading: 60,
        tilt: 55,
        zoom: 18,
        targetPosition: {
          lat: 58.58796004694554,
          lng: 16.18639260626686,
        },
      },
    },
    {
      id: 'tradgarn',
      title: "Trädgår'n Norrköping",
      visualizationType: 'marker',
      description: 'Restaurang och bar utomhus med möjlighet till live-scen.',
      images: ['tradgarn-1.jpg', 'tradgarn-2.jpg'],
      properties: {
        hasElectricity: true,
        hasRestaurantOrBar: true,
        audienceCapacity: '300 / 800 personer',
        purpose: ['Scenframträdande', 'Festlokal'],
        website: 'https://tradgarnuteservering.se/kontakt/',
      },
      model: {
        modelPath: '/models/pin.glb',
        position: new Vector3(0, 0, 0),
        scale: 7.0,
        rotationZ: 0,
      },
      cameraParameters: {
        heading: 60,
        tilt: 55,
        zoom: 18,
        targetPosition: {
          lat: 58.587797689166365,
          lng: 16.18618823237229,
        },
      },
    },
    {
      id: 'rumpan-bar',
      title: 'RumpanBar',
      visualizationType: 'marker',
      description: 'Lokal bar och populär live-scen.',
      images: ['rumpan bar-1.jpg', 'rumpan bar-2.jpg'],
      properties: {
        hasElectricity: true,
        hasRestaurantOrBar: true,
        audienceCapacity: '100 personer',
        purpose: ['Scenframträdande'],
        website: 'https://www.rumpanbar.nu/kontakt',
      },
      model: {
        modelPath: '/models/pin.glb',
        position: new Vector3(0, 0, 0),
        scale: 7.0,
        rotationZ: 0,
      },
      cameraParameters: {
        heading: 60,
        tilt: 55,
        zoom: 18,
        targetPosition: {
          lat: 58.586953945814045,
          lng: 16.176287217042802,
        },
      },
    },
    {
      id: 'borgen',
      title: 'Borgen',
      visualizationType: 'marker',
      description: 'Stor lokal som kan användas för fester med live-musik och stora dansgolv.',
      images: ['borgen-1.jpg', 'borgen-2.jpg', 'borgen-3.jpg', 'borgen-4.jpg'],
      properties: {
        hasElectricity: true,
        hasRestaurantOrBar: false,
        audienceCapacity: '200 / 550 / 800 / 2000 personer',
        purpose: ['Scenframträdande', 'Bröllop', 'Festlokal'],
        website: 'https://borgennorrkoping.com/kontakt/',
      },
      model: {
        modelPath: '/models/pin.glb',
        position: new Vector3(0, 0, 0),
        scale: 7.0,
        rotationZ: 0,
      },
      cameraParameters: {
        heading: 60,
        tilt: 55,
        zoom: 18,
        targetPosition: {
          lat: 58.57752032387539,
          lng: 16.1483725935769,
        },
      },
    },
  ]

  return slides
}
